import { useState, useCallback } from 'react';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import { useLocale } from '@adsk/alloy-react-locale';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';

/**
 * This hook handles remove entity process & return whether we need to show remove confirmation modal, show notification error etc.
 *
 * @param onRemove - a method returns a promise to handle relationship removal.
 * @returns {{dataToConfirmRemoving: *, closeConfirmRemoveModal: *, setDataToConfirmRemoving: *, isRemoving: *, handleRemoveConfirm: *}}
 */
export default (onRemove) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const { showNotification } = useNotifications();
  const [isRemoving, setIsRemoving] = useState(false);
  const [dataToConfirmRemoving, setDataToConfirmRemovingState] = useState(null);
  const closeConfirmRemoveModal = () => setDataToConfirmRemovingState(null);
  const projectId = useCurrentProjectId();
  const handleRemoveConfirm = () => {
    setIsRemoving(true);
    onRemove(dataToConfirmRemoving.relationship)
      .then(() => {
        if (dataToConfirmRemoving.plugin.onRemove) {
          dataToConfirmRemoving.plugin.onRemove(
            dataToConfirmRemoving.relationship,
            dataToConfirmRemoving.entity,
            projectId,
          );
        }
        closeConfirmRemoveModal();
        setIsRemoving(false);
      })
      .catch((error) => {
        console.error(
          'ReferencePicker - useRemoveEntity: Error removing relationship',
          error,
        );
        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: T.translate('ERROR_REMOVING_RELATIONSHIPS_MESSAGE'),
        });
        setIsRemoving(false);
      });
  };

  const setDataToConfirmRemoving = useCallback(
    ({ relationship, entity, plugin }) => {
      setDataToConfirmRemovingState({
        relationship,
        entity,
        plugin,
      });
    },
    [],
  );

  return {
    dataToConfirmRemoving,
    setDataToConfirmRemoving,
    isRemoving,
    closeConfirmRemoveModal,
    handleRemoveConfirm,
  };
};
